/**
 * PilotContext — the React-owned state surface for /pilot.
 *
 * Holds `viewMode`, `audience`, `isAdmin`, intake filters, knob
 * values, and `currentProcessId`. The provider's `useEffect` is the
 * single writer of the view-mode body class — at most one of
 * `story-mode | pitch-mode | demo-mode | vis-mode` is ever on
 * `<body>` at a given instant. Sandbox shares the `story-mode` body
 * class with Story (additive scroll). Demo additionally locks
 * `document.body.style.overflow` so its internal scroll doesn't
 * compound with the page scroll.
 *
 * The architectural invariant this context enforces:
 *
 *   > React is the only writer of pilot view-mode body classes. Files
 *   > under `apps/web/src/lib/pilot/` MUST NOT call
 *   > `document.body.classList.*` for view-mode classes. The provider's
 *   > effect below is the single writer; everything else reads
 *   > `viewMode` from the hook.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  DEFAULT_PARAMS,
  setParams as syncLegacyParams,
  type ParamId,
  type SimParams,
} from '../../lib/pilot/params';

import type {Intake} from '../../lib/pilot/processes';
import {PROCESSES} from '../../lib/pilot/processes';
import {
  INTAKE_CHANGED_EVENT,
  INTAKE_SET_REQUEST_EVENT,
  type IntakeSetRequestDetail,
} from '../../lib/pilot/intake-bridge';
import {
  applyUrlStateToStorage,
  consumeUrlDeepLinkProcessId,
  peekUrlDeepLinkProcessId,
} from '../../lib/pilot/url-state';

// Phase J of the imperative-renderer retirement: parse `window.location.search`
// and seed mp-talk-lead / mp-email / mp-intake / mp-params BEFORE the
// provider's useState initializers read them. This single module-load
// side-effect replaces main.js's `hydrateIdentityFromUrl()` boot call.
applyUrlStateToStorage();

export type ViewMode = 'story' | 'sandbox' | 'pitch' | 'demo' | 'vision';
export type PilotAudience = 'CUSTOMER' | 'VC';
export type IntakeField = keyof Intake;

/** Sandbox knob values, keyed by `ParamId`. Mirrors `SimParams` shape. */
export type KnobValues = SimParams;

const VIEW_MODE_BODY_CLASSES: ReadonlyArray<string> = [
  'story-mode',
  'demo-mode',
  'vis-mode',
  'pitch-mode',
];

// Map from viewMode to the body class CSS rules in pilot-engine.css
// still consume (only the `body:not(.story-mode) .story-dots` hide
// rule survives). Story and Sandbox share the `story-mode` body
// class because they share scroll.
function bodyClassFor(mode: ViewMode): string | null {
  switch (mode) {
    case 'story':
    case 'sandbox':
      return 'story-mode';
    case 'demo':
      return 'demo-mode';
    case 'vision':
      return 'vis-mode';
    case 'pitch':
      return 'pitch-mode';
  }
}

// localStorage keys — chosen to be back-compat with the storage
// payloads written by earlier versions of the pilot, so a returning
// visitor lands in the same view / audience / process / knob state.
const MODE_KEY = 'mp-mode';
const AUDIENCE_KEY = 'mp-invite-audience';
const ADMIN_TOKEN_KEY = 'planapus-admin-token';
const PARAMS_KEY = 'mp-params';
const PROCESS_KEY = 'mp-last-process';

// Intake state (industry/size/function). The read-only bridge under
// `lib/pilot/intake-bridge.ts` exposes the same value to the surviving
// imperative consumers (story-render.js, pilot-engine.js).
const INTAKE_KEY = 'mp-intake';
const EMPTY_INTAKE: Intake = {industry: '', size: '', function: ''};

function readStoredIntake(): Intake {
  if (typeof window === 'undefined') return {...EMPTY_INTAKE};
  let stored: Partial<Intake> = {};
  try {
    const raw = window.localStorage.getItem(INTAKE_KEY);
    if (raw) stored = JSON.parse(raw) as Partial<Intake>;
  } catch {
    /* private mode or malformed JSON */
  }
  return {
    industry: typeof stored.industry === 'string' ? stored.industry : '',
    size: typeof stored.size === 'string' ? stored.size : '',
    function: typeof stored.function === 'string' ? stored.function : '',
  };
}

function readUrlIntake(): Partial<Intake> {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const industry = params.get('industry') || '';
    const size = params.get('size') || '';
    // Legacy share links used either `function` or the shorter `func`.
    const fn = params.get('function') || params.get('func') || '';
    const out: Partial<Intake> = {};
    if (industry) out.industry = industry;
    if (size) out.size = size;
    if (fn) out.function = fn;
    return out;
  } catch {
    return {};
  }
}

function hydrateIntake(): Intake {
  // URL params win over stored values so personalized share links
  // (?industry=saas&function=ops) always land on the targeted view.
  const stored = readStoredIntake();
  const fromUrl = readUrlIntake();
  return {
    industry: fromUrl.industry || stored.industry,
    size: fromUrl.size || stored.size,
    function: fromUrl.function || stored.function,
  };
}

// Audience -> allowed routes. PilotNavbar reads this to gate the
// per-audience nav-item list.
const AUDIENCE_TABS: Record<PilotAudience, ReadonlyArray<ViewMode>> = {
  CUSTOMER: ['story', 'sandbox'],
  VC: ['pitch', 'story', 'sandbox'],
};

function readAudience(): PilotAudience {
  if (typeof window === 'undefined') return 'CUSTOMER';
  try {
    return window.localStorage.getItem(AUDIENCE_KEY) === 'VC'
      ? 'VC'
      : 'CUSTOMER';
  } catch {
    return 'CUSTOMER';
  }
}

function readIsAdmin(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return !!window.localStorage.getItem(ADMIN_TOKEN_KEY);
  } catch {
    return false;
  }
}

function readStoredMode(): ViewMode | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(MODE_KEY);
    if (
      v === 'story' ||
      v === 'sandbox' ||
      v === 'pitch' ||
      v === 'demo' ||
      v === 'vision'
    ) {
      return v;
    }
  } catch {
    /* private mode */
  }
  return null;
}

function readStoredParams(): KnobValues {
  if (typeof window === 'undefined') return {...DEFAULT_PARAMS};
  try {
    const raw: unknown = JSON.parse(
      window.localStorage.getItem(PARAMS_KEY) || 'null',
    );
    if (raw && typeof raw === 'object') {
      const obj = raw as Record<string, unknown>;
      const out: KnobValues = {...DEFAULT_PARAMS};
      for (const k of Object.keys(DEFAULT_PARAMS) as ParamId[]) {
        const v = obj[k];
        if (typeof v === 'number' && isFinite(v)) {
          out[k] = v;
        }
      }
      return out;
    }
  } catch {
    /* private mode / malformed */
  }
  return {...DEFAULT_PARAMS};
}

function readStoredProcess(): string | null {
  // First-class consumer of the URL deep-link (`?process=<id>`).
  // applyUrlStateToStorage() ran at module load so the deep-link is
  // already cached; consumeUrlDeepLinkProcessId() returns + clears it.
  // Sandbox.tsx's effect on currentProcessId dispatches
  // `pilot:process-selected` so claude.ts + modals.js still see the
  // same event.
  const deepLink = consumeUrlDeepLinkProcessId();
  if (deepLink && Object.prototype.hasOwnProperty.call(PROCESSES, deepLink)) {
    return deepLink;
  }
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(PROCESS_KEY);
    if (stored && Object.prototype.hasOwnProperty.call(PROCESSES, stored)) {
      return stored;
    }
  } catch {
    /* private mode */
  }
  return null;
}

function defaultRouteFor(
  audience: PilotAudience,
  isAdmin: boolean,
): ViewMode {
  // Audience-default first; admin can also reach demo/vision but those
  // aren't the *default* landing — even admins land on the audience's
  // first route.
  void isAdmin;
  return AUDIENCE_TABS[audience][0];
}

function canRoute(
  mode: ViewMode,
  audience: PilotAudience,
  isAdmin: boolean,
): boolean {
  if (mode === 'demo' || mode === 'vision') return isAdmin;
  return AUDIENCE_TABS[audience].includes(mode);
}

interface PilotContextValue {
  viewMode: ViewMode;
  audience: PilotAudience;
  isAdmin: boolean;
  intake: Intake;
  setViewMode: (mode: ViewMode) => void;
  setIntakeField: (field: IntakeField, value: string) => void;
  canRoute: (mode: ViewMode) => boolean;
  /** Sandbox knob values; persisted to `mp-params`. */
  knobs: KnobValues;
  /** Set one knob; persists and notifies subscribers. */
  setKnob: (id: ParamId, value: number) => void;
  /** Reset every knob to its default value. */
  resetKnobs: () => void;
  /** Currently picked process id; null before pick. Persisted to `mp-last-process`. */
  currentProcessId: string | null;
  /** Record a process pick; persists to `mp-last-process`. */
  selectProcess: (processId: string) => void;
  /**
   * Whether the Sandbox view is reachable. Derived from currentProcessId.
   * Replaces the legacy `body.pp-process-selected` body class read that
   * PilotNavbar used to observe via MutationObserver (Phase 1 of the
   * Oatmeal rebuild, F6 from PR #59 review).
   */
  sandboxUnlocked: boolean;
}

const PilotStateContext = createContext<PilotContextValue | null>(null);

/**
 * Custom event name dispatched when localStorage's auth-related keys
 * (audience, admin token) change in the same tab. Writers should
 * `window.dispatchEvent(new Event(PILOT_AUTH_CHANGED_EVENT))` after
 * `localStorage.setItem(AUDIENCE_KEY|ADMIN_TOKEN_KEY, …)` so the
 * PilotProvider's subscriber picks up the change without a reload.
 * F9 of the Oatmeal rebuild.
 */
export const PILOT_AUTH_CHANGED_EVENT = 'pilot:auth-changed';

/** Convenience helper for writers to fire the auth-changed event. */
export function dispatchPilotAuthChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(PILOT_AUTH_CHANGED_EVENT));
  } catch {
    /* very old browsers; not a real concern */
  }
}

export interface PilotProviderProps {
  children: React.ReactNode;
}

export function PilotProvider({children}: PilotProviderProps) {
  // F9 — same-tab audience / admin updates.
  //
  // Previously these were `useMemo(readX, [])`, so a returning visitor's
  // invite-validation write to localStorage only surfaced after a hard
  // reload. Now they're state, re-read on three triggers:
  //
  //   - `storage` event (browser-native; fires on OTHER tabs when
  //     localStorage changes — covers cross-tab updates).
  //   - `pilot:auth-changed` custom event (same-tab; writers call
  //     `dispatchPilotAuthChanged()` after `setItem` to notify).
  //   - tab `focus` (defense in depth — if a writer somewhere forgot
  //     the dispatch and the user switched away+back, we still resync).
  //
  // The ADMIN_TOKEN_KEY writer lives outside this module (legacy admin
  // login flow). It won't fire `pilot:auth-changed` on same-tab writes,
  // but the focus rescan picks it up on next interaction.
  const [audience, setAudience] = useState<PilotAudience>(readAudience);
  const [isAdmin, setIsAdmin] = useState<boolean>(readIsAdmin);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const refresh = () => {
      setAudience(readAudience());
      setIsAdmin(readIsAdmin());
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === AUDIENCE_KEY || e.key === ADMIN_TOKEN_KEY) refresh();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(PILOT_AUTH_CHANGED_EVENT, refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(PILOT_AUTH_CHANGED_EVENT, refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    // URL deep-link wins: if the visitor opened a `?process=<id>` link
    // they shared "look at THESE numbers" — land them in Sandbox even
    // if the stored mode is story/pitch.
    const deepLink = peekUrlDeepLinkProcessId();
    if (
      deepLink &&
      Object.prototype.hasOwnProperty.call(PROCESSES, deepLink) &&
      canRoute('sandbox', audience, isAdmin)
    ) {
      return 'sandbox';
    }
    const stored = readStoredMode();
    // Sandbox is gated by a picked use case. If the stored mode is
    // sandbox but there's no stored process (cleared cache, fresh visit
    // from a stale tab), fall back to story so the visitor lands on the
    // picker instead of an empty notebook shell. Per the May 2026
    // functional/content review: Sandbox shouldn't work without a use
    // case selected.
    if (stored === 'sandbox' && !readStoredProcess()) {
      return defaultRouteFor(audience, isAdmin);
    }
    if (stored && canRoute(stored, audience, isAdmin)) return stored;
    return defaultRouteFor(audience, isAdmin);
  });

  // Intake (industry/size/function) — Phase 2 of the React-engine port.
  // Hydrated once from URL params + localStorage; subsequent writes
  // persist to localStorage and dispatch `pilot:intake-changed` so the
  // surviving vanilla-JS engine can re-render dependent UI via
  // `intake-bridge.onIntakeChange`.
  const [intake, setIntakeState] = useState<Intake>(hydrateIntake);

  // Knobs + selected process — Phase 5. Knobs persist to `mp-params`,
  // process to `mp-last-process` (key compat with legacy params.ts).
  const [knobs, setKnobsState] = useState<KnobValues>(() => readStoredParams());
  const [currentProcessId, setCurrentProcessIdState] = useState<string | null>(
    () => readStoredProcess(),
  );

  // Sole writer of view-mode body classes. Any attempt elsewhere to
  // set or clear `story-mode` / `pitch-mode` / `demo-mode` / `vis-mode`
  // on <body> is a regression and should be deleted.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const desired = bodyClassFor(viewMode);
    for (const cls of VIEW_MODE_BODY_CLASSES) {
      document.body.classList.toggle(cls, cls === desired);
    }
    // `.tldr-pinned` is an additive flag SandboxTldrStrip toggles
    // while the visitor is scrolled past the wins block. Takeovers
    // don't share that scroll, so clear it on the way out.
    if (desired !== 'story-mode') {
      document.body.classList.remove('tldr-pinned');
    }
    // Demo takes over the full viewport with its own scroll-snap
    // scroller — lock the body scroll so the demo's internal scroll
    // doesn't double up with the page scroll. F17 Slice D replaced
    // the prior `body.demo-mode { overflow: hidden }` CSS rule.
    const prevOverflow = document.body.style.overflow;
    if (viewMode === 'demo') document.body.style.overflow = 'hidden';
    return () => {
      // Restore whatever was there before we touched it.
      if (viewMode === 'demo') document.body.style.overflow = prevOverflow;
    };
  }, [viewMode]);

  // Persist the chosen mode so a returning visitor lands where they
  // left. Same key as legacy main.js used.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(MODE_KEY, viewMode);
    } catch {
      /* private mode */
    }
  }, [viewMode]);

  // Expose the audience as a body data attribute so legacy CSS
  // selectors keyed off `body[data-audience="VC"]` still resolve.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.setAttribute('data-audience', audience);
  }, [audience]);

  // Persist + notify on intake change (Phase 2). The vanilla-JS engine
  // subscribes via `intake-bridge.onIntakeChange` which listens for
  // INTAKE_CHANGED_EVENT — keep the dispatch in sync with the persist
  // so engine reads through `intake-bridge.getIntake()` are consistent
  // with what subscribers see.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(INTAKE_KEY, JSON.stringify(intake));
    } catch {
      /* private mode */
    }
    try {
      window.dispatchEvent(new CustomEvent(INTAKE_CHANGED_EVENT));
    } catch {
      /* CustomEvent unsupported (IE-era; not a real concern) */
    }
  }, [intake]);

  // Persist knob values whenever they change. Format matches the
  // `lib/pilot/params.ts` payload so a returning visitor's existing
  // dial stays intact. Also forwards into that store so its
  // `onParamsChange` listeners (used by the surviving imperative
  // paint targets in story-render.js) keep firing.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(PARAMS_KEY, JSON.stringify(knobs));
    } catch {
      /* private mode */
    }
    syncLegacyParams(knobs);
  }, [knobs]);

  // Persist the currently picked process id under `mp-last-process`.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (currentProcessId) {
        window.localStorage.setItem(PROCESS_KEY, currentProcessId);
      } else {
        window.localStorage.removeItem(PROCESS_KEY);
      }
    } catch {
      /* private mode */
    }
  }, [currentProcessId]);

  const setViewMode = useCallback(
    (mode: ViewMode) => {
      // Audience-forbidden routes silently redirect to the audience
      // default — same behavior as legacy applyRoute().
      let target = canRoute(mode, audience, isAdmin)
        ? mode
        : defaultRouteFor(audience, isAdmin);
      // Sandbox is gated on a picked use case. If the caller tries to
      // route into Sandbox without one, redirect to Story (where the
      // picker lives). Per the May 2026 functional/content review,
      // empty-Sandbox shouldn't be reachable.
      if (target === 'sandbox' && !currentProcessId) {
        target = 'story';
      }
      setViewModeState(target);
    },
    [audience, isAdmin, currentProcessId],
  );

  const canRouteForCaller = useCallback(
    (mode: ViewMode) => canRoute(mode, audience, isAdmin),
    [audience, isAdmin],
  );

  const setIntakeField = useCallback((field: IntakeField, value: string) => {
    setIntakeState(prev => {
      const next = prev[field];
      const incoming = value || '';
      if (next === incoming) return prev;
      return {...prev, [field]: incoming};
    });
  }, []);

  // Bridge inbound: the surviving vanilla-JS engine (e.g. the sandbox
  // empty-state "clear filter" button) dispatches
  // `pilot:set-intake-field` to request a write. React stays the only
  // *writer* of intake — this handler just translates the event into a
  // setIntakeField call.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (evt: Event) => {
      const ce = evt as CustomEvent<IntakeSetRequestDetail>;
      const detail = ce.detail;
      if (!detail || typeof detail.field !== 'string') return;
      if (
        detail.field !== 'industry' &&
        detail.field !== 'size' &&
        detail.field !== 'function'
      ) {
        return;
      }
      setIntakeField(detail.field, detail.value || '');
    };
    window.addEventListener(INTAKE_SET_REQUEST_EVENT, handler);
    return () =>
      window.removeEventListener(INTAKE_SET_REQUEST_EVENT, handler);
  }, [setIntakeField]);

  const setKnob = useCallback((id: ParamId, value: number) => {
    if (typeof value !== 'number' || !isFinite(value)) return;
    setKnobsState((prev) =>
      prev[id] === value ? prev : {...prev, [id]: value},
    );
  }, []);

  const resetKnobs = useCallback(() => {
    setKnobsState({...DEFAULT_PARAMS});
  }, []);

  const selectProcess = useCallback((processId: string) => {
    if (!processId) return;
    setCurrentProcessIdState(processId);
  }, []);

  const sandboxUnlocked = currentProcessId !== null;

  const value = useMemo<PilotContextValue>(
    () => ({
      viewMode,
      audience,
      isAdmin,
      intake,
      setViewMode,
      setIntakeField,
      canRoute: canRouteForCaller,
      knobs,
      setKnob,
      resetKnobs,
      currentProcessId,
      selectProcess,
      sandboxUnlocked,
    }),
    [
      viewMode,
      audience,
      isAdmin,
      intake,
      setViewMode,
      setIntakeField,
      canRouteForCaller,
      knobs,
      setKnob,
      resetKnobs,
      currentProcessId,
      selectProcess,
      sandboxUnlocked,
    ],
  );

  return (
    <PilotStateContext.Provider value={value}>
      {children}
    </PilotStateContext.Provider>
  );
}

export function usePilot(): PilotContextValue {
  const value = useContext(PilotStateContext);
  if (!value) {
    throw new Error(
      'usePilot must be called inside a <PilotProvider>. ' +
        'Mount PilotProvider above any component that calls this hook.',
    );
  }
  return value;
}
